import { isCreverseSsoTokenInvalidError } from '@mathflat/creverse-auth/errors'
import {
  CREVERSE_TEACHER_LOGIN_PATH,
  CREVERSE_TEACHER_STAFF_INFO_PATH,
  getCreverseStaffInfo as fetchCreverseStaffInfo,
  loginCreverseStaff,
} from '@mathflat/creverse-auth/two-phase'
import type {
  CreverseTeacherLoginRequest,
  CreverseTeacherStaffInfoRequest,
} from '@mathflat/domain/Auth'
import { CREVERSE_GUEST_SYNC_PATH } from '@mathflat/domain/Guest'
import { jwtTokenService } from '@mathflat/shared/@modules/auth/jwtTokenService'

import { creverseFetcher } from '@/api/creverseFetcher'
import type { CommonError } from '@/dto'
import { RouteName } from '@/router'
import { authService } from '@/service/auth.service'
import { localStorageService } from '@/service/storage.service'
import { reporter } from '@/utils/modules/errorReporting/reporter'
import { toastError } from '@/utils/toast'

export { isCreverseSsoTokenInvalidError }

const clearCreverseSession = () => {
  jwtTokenService.clearTokens()
  reporter.setUser(null)
  localStorageService.removeItem('CREVERSE_SSO_TOKEN')
}

// 세션 자체가 주제인 요청(SSO 진입·명단 동기화)만 만료를 알린다.
// 그 밖의 기능 요청은 사용자가 누른 동작이 무엇인지 모르므로 포괄 문구를 쓴다.
const SESSION_AXIS_PATHS: string[] = [
  CREVERSE_TEACHER_STAFF_INFO_PATH,
  CREVERSE_TEACHER_LOGIN_PATH,
  CREVERSE_GUEST_SYNC_PATH,
]

const resolveExpiredMessage = (requestPath: string | undefined) =>
  requestPath && SESSION_AXIS_PATHS.includes(requestPath)
    ? '로그인 정보가 만료되었어요. 다시 로그인해 주세요.'
    : '요청한 작업에 실패했습니다.\n다시 로그인 후 시도해 주세요.'

const redirectToCreverseLogin = (requestPath: string | undefined) => {
  toastError(resolveExpiredMessage(requestPath))
  clearCreverseSession()
  window.location.replace(RouteName.creverseLogin)
}

export const handleCreverseSsoTokenError = (error: unknown) => {
  if (!isCreverseSsoTokenInvalidError(error)) {
    return false
  }

  redirectToCreverseLogin((error as CommonError).config?.url)

  return true
}

// 1페이즈: 강사정보(지점·서명) 조회 후, 서버가 받아들인 ssoToken 만 보관한다
export const getCreverseStaffInfo = async (
  ssoToken: CreverseTeacherStaffInfoRequest['ssoToken'],
) => {
  const staffInfo = await fetchCreverseStaffInfo(creverseFetcher, ssoToken)
  localStorageService.setItem('CREVERSE_SSO_TOKEN')(ssoToken)

  return staffInfo
}

// 2페이즈: 서명+선택 지점으로 로그인. accessToken 없는/선생님 아닌 응답은 null 로 돌려 호출부가 실패 처리한다.
// (accessToken 없는 응답을 finalizeJwtLogin 에 넘기면 빈 Bearer→401→clearTokens 로 정상 세션까지 삭제된다)
export const loginByCreverse = async (loginRequest: CreverseTeacherLoginRequest) => {
  const data = await loginCreverseStaff(creverseFetcher, loginRequest)

  if (!data?.accessToken || data.userType !== 'TEACHER') {
    return null
  }

  await authService.finalizeJwtLogin(data)

  return data
}
